import { createSelector, createSlice } from "@reduxjs/toolkit";
import bothiesData from "../data/bothies.json";
import { createDefaultFilters, NATURAL_FEATURE_OPTIONS } from "../constants/bothies";

const normalizedBothies = bothiesData
  .filter(bothy => Number.isFinite(Number(bothy.lat)) && Number.isFinite(Number(bothy.lon)))
  .map((bothy, index) => normalizeBothy(bothy, index));

const initialState = {
  entities: normalizedBothies,
  searchTerm: "",
  showFilters: false,
  filters: createDefaultFilters(),
  selectedBothyId: null
};

const bothiesSlice = createSlice({
  name: "bothies",
  initialState,
  reducers: {
    setSelectedBothy(state, action) {
      state.selectedBothyId = action.payload;
    },
    clearSelectedBothy(state) {
      state.selectedBothyId = null;
    },
    setSearchTerm(state, action) {
      state.searchTerm = action.payload;
    },
    toggleFilters(state) {
      state.showFilters = !state.showFilters;
    },
    toggleBothyFeatureFilter(state, action) {
      const feature = action.payload;
      const currentFeatures = state.filters.bothyFeatures;
      if (currentFeatures.includes(feature)) {
        state.filters.bothyFeatures = currentFeatures.filter(item => item !== feature);
      } else {
        state.filters.bothyFeatures.push(feature);
      }
    },
    toggleNaturalFeatureFilter(state, action) {
      const feature = action.payload;
      state.filters.naturalFeatures[feature] = !state.filters.naturalFeatures[feature];
    },
    setElevationFilter(state, action) {
      const { field, value } = action.payload;
      state.filters.elevation[field] = normalizeNumericFilterValue(value);
    },
    setRoadDistanceFilter(state, action) {
      const { field, value } = action.payload;
      state.filters.roadDistance[field] = normalizeNumericFilterValue(value);
    },
    setBortleClassFilter(state, action) {
      const value = action.payload;
      state.filters.bortleClass = value === "" ? "" : Number(value);
    },
    setWeatherFilter(state, action) {
      const { field, value } = action.payload;
      state.filters.weather[field] = normalizeNumericFilterValue(value);
    },
    hydrateFilters(state, action) {
      const { filters, searchTerm, showFilters } = action.payload || {};
      if (filters) {
        const defaultFilters = createDefaultFilters();
        state.filters = {
          ...defaultFilters,
          ...filters,
          naturalFeatures: {
            ...defaultFilters.naturalFeatures,
            ...(filters.naturalFeatures || {})
          }
        };
      }
      if (typeof searchTerm === "string") {
        state.searchTerm = searchTerm;
      }
      if (typeof showFilters === "boolean") {
        state.showFilters = showFilters;
      }
    },
    clearFilters(state) {
      state.filters = createDefaultFilters();
      state.searchTerm = "";
    }
  }
});

export const {
  setSelectedBothy,
  clearSelectedBothy,
  setSearchTerm,
  toggleFilters,
  toggleBothyFeatureFilter,
  toggleNaturalFeatureFilter,
  setElevationFilter,
  setRoadDistanceFilter,
  setBortleClassFilter,
  setWeatherFilter,
  hydrateFilters,
  clearFilters
} = bothiesSlice.actions;

export default bothiesSlice.reducer;

const selectBothiesState = state => state.bothies;
const selectWeatherByBothyId = state => state.weather?.forecastsByBothyId ?? {};

export const selectAllBothies = createSelector(
  [selectBothiesState],
  bothiesState => bothiesState.entities
);

export const selectAllBothiesCount = createSelector(
  [selectAllBothies],
  bothies => bothies.length
);

export const selectSearchTerm = createSelector(
  [selectBothiesState],
  bothiesState => bothiesState.searchTerm
);

export const selectShowFilters = createSelector(
  [selectBothiesState],
  bothiesState => bothiesState.showFilters
);

export const selectFilters = createSelector(
  [selectBothiesState],
  bothiesState => bothiesState.filters
);

export const selectSelectedBothyId = createSelector(
  [selectBothiesState],
  bothiesState => bothiesState.selectedBothyId
);

export const selectSelectedBothy = createSelector(
  [selectAllBothies, selectSelectedBothyId],
  (bothies, selectedId) => bothies.find(bothy => bothy.id === selectedId) ?? null
);

export const selectAvailableBothyFeatures = createSelector(
  [selectAllBothies],
  bothies => {
    const featureSet = new Set();
    bothies.forEach(bothy => {
      bothy.bothyFeatures.forEach(feature => featureSet.add(feature));
    });
    return Array.from(featureSet).sort();
  }
);

export const selectAvailableBortleClasses = createSelector(
  [selectAllBothies],
  bothies => {
    const classes = new Set();
    bothies.forEach(bothy => {
      if (typeof bothy.bortleClass === "number") {
        classes.add(bothy.bortleClass);
      }
    });
    return Array.from(classes).sort((a, b) => a - b);
  }
);

export const selectVisibleBothies = createSelector(
  [selectAllBothies, selectFilters, selectSearchTerm, selectWeatherByBothyId],
  (bothies, filters, searchTerm, weatherByBothyId) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const naturalFilters = Object.entries(filters.naturalFeatures)
      .filter(([, isActive]) => isActive)
      .map(([key]) => key);

    return bothies
      .filter(bothy => {
        if (normalizedSearch && !bothy.searchableName.includes(normalizedSearch)) {
          return false;
        }

        if (
          filters.bothyFeatures.length > 0 &&
          !filters.bothyFeatures.every(feature => bothy.bothyFeatures.includes(feature))
        ) {
          return false;
        }

        if (
          naturalFilters.length > 0 &&
          !naturalFilters.every(feature => bothy.features?.[feature])
        ) {
          return false;
        }

        if (
          filters.elevation.min !== "" &&
          bothy.elevation < filters.elevation.min
        ) {
          return false;
        }

        if (
          filters.elevation.max !== "" &&
          bothy.elevation > filters.elevation.max
        ) {
          return false;
        }

        if (
          filters.bortleClass !== "" &&
          bothy.bortleClass !== Number(filters.bortleClass)
        ) {
          return false;
        }

        const roadDistance = bothy.nearestRoad?.distance;
        if (
          filters.roadDistance.min !== "" &&
          (roadDistance === undefined || roadDistance < filters.roadDistance.min)
        ) {
          return false;
        }

        if (
          filters.roadDistance.max !== "" &&
          (roadDistance === undefined || roadDistance > filters.roadDistance.max)
        ) {
          return false;
        }

        const weatherEntry = weatherByBothyId[bothy.id];
        if (!matchesWeatherFilters(filters.weather, weatherEntry?.data, weatherEntry?.snow)) {
          return false;
        }

        return true;
      })
      .slice()
      .sort((a, b) => {
        const distanceA = a?.nearestRoad?.distance ?? -Infinity;
        const distanceB = b?.nearestRoad?.distance ?? -Infinity;
        return distanceB - distanceA;
      });
  }
);

function normalizeBothy(bothy, index) {
  const lat = Number(bothy.lat);
  const lon = Number(bothy.lon);
  const elevation = Number(bothy.elevation);
  const bothyFeatures = Array.isArray(bothy.bothyFeatures)
    ? bothy.bothyFeatures
    : [];

  const nearestRoad = bothy.nearestRoad
    ? {
        ...bothy.nearestRoad,
        distance: bothy.nearestRoad.distance
          ? Number(bothy.nearestRoad.distance)
          : undefined
      }
    : undefined;

  const defaultFeatures = buildDefaultFeatures();
  return {
    ...bothy,
    id: createBothyId(bothy.name, index),
    lat,
    lon,
    elevation: Number.isNaN(elevation) ? undefined : elevation,
    bortleClass:
      bothy.bortleClass !== undefined ? Number(bothy.bortleClass) : undefined,
    brightness:
      bothy.brightness !== undefined ? Number(bothy.brightness) : undefined,
    bothyFeatures,
    features: {
      ...defaultFeatures,
      ...(bothy.features ?? {})
    },
    nearestRoad,
    closestDistance:
      bothy.closestDistance !== undefined
        ? Number(bothy.closestDistance)
        : undefined,
    searchableName: bothy.name.toLowerCase()
  };
}

function createBothyId(name, index) {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `${slug || "bothy"}-${index}`;
}

function buildDefaultFeatures() {
  return NATURAL_FEATURE_OPTIONS.reduce(
    (acc, feature) => {
      acc[feature.key] = false;
      return acc;
    },
    {}
  );
}

function normalizeNumericFilterValue(value) {
  if (value === "" || value === null || value === undefined) {
    return "";
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : "";
}

function matchesWeatherFilters(weatherFilters, forecastData, snowData) {
  if (!weatherFilters) {
    return true;
  }

  const {
    maxPrecipitation,
    maxRainfall,
    maxSnowfall,
    minSnowDepth,
    maxWindSpeed,
    maxPrecipitationHours,
    minDailyLow,
    maxDailyHigh,
    minSnowfall,
    targetDayIndex
  } = weatherFilters;
  const requiresWeatherData =
    maxPrecipitation !== "" ||
    maxRainfall !== "" ||
    maxSnowfall !== "" ||
    minSnowDepth !== "" ||
    minSnowfall !== "" ||
    maxWindSpeed !== "" ||
    maxPrecipitationHours !== "" ||
    minDailyLow !== "" ||
    maxDailyHigh !== "" ||
    targetDayIndex !== "";

  if (!requiresWeatherData) {
    return true;
  }

  const daily = forecastData?.daily ?? snowData?.daily;
  if (!daily) {
    return false;
  }

  const dayIndex = targetDayIndex === "" ? null : targetDayIndex;

  if (maxPrecipitation !== "") {
    const precipitationValues = extractNumericValues(daily.precipitation_sum, dayIndex);
    if (!precipitationValues.length) {
      return false;
    }
    if (Math.max(...precipitationValues) > maxPrecipitation) {
      return false;
    }
  }

  if (maxRainfall !== "") {
    const rainValues = extractNumericValues(daily.rain_sum, dayIndex);
    if (!rainValues.length) {
      return false;
    }
    if (Math.max(...rainValues) > maxRainfall) {
      return false;
    }
  }

  if (maxSnowfall !== "") {
    const snowfallValues = extractNumericValues(
      snowData?.daily?.snowfall_sum ?? daily.snowfall_sum,
      dayIndex
    );
    if (!snowfallValues.length) {
      return false;
    }
    if (Math.max(...snowfallValues) > maxSnowfall) {
      return false;
    }
  }

  if (minSnowfall !== "") {
    const snowfallValues = extractNumericValues(
      snowData?.daily?.snowfall_sum ?? daily.snowfall_sum,
      dayIndex
    );
    if (!snowfallValues.length) {
      return false;
    }
    if (Math.max(...snowfallValues) < minSnowfall) {
      return false;
    }
  }

  if (minSnowDepth !== "") {
    const depthUnit =
      snowData?.daily_units?.snow_depth_max ??
      forecastData?.daily_units?.snow_depth_max;
    const snowDepthValues = extractNumericValues(
      snowData?.daily?.snow_depth_max ?? daily.snow_depth_max,
      dayIndex
    ).map(value => convertDepthToMm(value, depthUnit));
    if (!snowDepthValues.length) {
      return false;
    }
    if (Math.max(...snowDepthValues) < minSnowDepth) {
      return false;
    }
  }

  if (maxPrecipitationHours !== "") {
    const precipitationHoursValues = extractNumericValues(daily.precipitation_hours, dayIndex);
    if (!precipitationHoursValues.length) {
      return false;
    }
    if (Math.max(...precipitationHoursValues) > maxPrecipitationHours) {
      return false;
    }
  }

  if (maxWindSpeed !== "") {
    const windValues = extractNumericValues(daily.wind_speed_10m_max, dayIndex);
    if (!windValues.length) {
      return false;
    }
    if (Math.max(...windValues) > maxWindSpeed) {
      return false;
    }
  }

  if (minDailyLow !== "") {
    const lowTemps = extractNumericValues(daily.temperature_2m_min, dayIndex);
    if (!lowTemps.length) {
      return false;
    }
    if (Math.min(...lowTemps) < minDailyLow) {
      return false;
    }
  }

  if (maxDailyHigh !== "") {
    const highTemps = extractNumericValues(daily.temperature_2m_max, dayIndex);
    if (!highTemps.length) {
      return false;
    }
    if (Math.max(...highTemps) > maxDailyHigh) {
      return false;
    }
  }

  return true;
}

function extractNumericValues(values, dayIndex) {
  if (!Array.isArray(values)) {
    return [];
  }

  if (dayIndex !== null) {
    const singleValue = values[dayIndex];
    const parsed = Number(singleValue);
    return Number.isFinite(parsed) ? [parsed] : [];
  }

  return values
    .map(value => Number(value))
    .filter(value => Number.isFinite(value));
}

function convertDepthToMm(value, unit) {
  if (!Number.isFinite(value)) {
    return value;
  }
  const lower = (unit || "").toLowerCase();
  if (lower === "m") return value * 1000;
  if (lower === "cm") return value * 10;
  return value;
}
